import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Building2, Users, Boxes, Loader2, ChevronRight, Copy, ExternalLink, Check } from 'lucide-react'
import api from '../../core/services/api'

interface Tenant {
  id: string
  code: string
  name: string
  description: string | null
  status: string
  email: string | null
  phone: string | null
  max_users: string
  created_at: string
  users_count: number
  modules_count: number
}

// Costruisce l'URL completo di login per un tenant
function getTenantLoginUrl(code: string): string {
  return `${window.location.origin}/${code}`
}

export default function TenantsPage() {
  const navigate = useNavigate()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  useEffect(() => {
    loadTenants()
  }, [])

  const loadTenants = async () => {
    try {
      const response = await api.get('/v1/superadmin/tenants')
      setTenants(response.data)
    } catch (err) {
      console.error('Errore caricamento tenant:', err)
    } finally {
      setLoading(false)
    }
  }

  const copyLink = async (tenant: Tenant, e: React.MouseEvent) => {
    e.stopPropagation() // Evita navigazione al dettaglio
    const url = getTenantLoginUrl(tenant.code)
    try {
      await navigator.clipboard.writeText(url)
      setCopiedId(tenant.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      // Fallback per browser che non supportano clipboard API
      const textarea = document.createElement('textarea')
      textarea.value = url
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedId(tenant.id)
      setTimeout(() => setCopiedId(null), 2000)
    }
  }

  const openLink = (tenant: Tenant, e: React.MouseEvent) => {
    e.stopPropagation() // Evita navigazione al dettaglio
    const url = getTenantLoginUrl(tenant.code)
    window.open(url, '_blank')
  }

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    trial: 'bg-blue-100 text-blue-700',
    suspended: 'bg-orange-100 text-orange-700',
    cancelled: 'bg-red-100 text-red-700',
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Tenant</h1>
          <p className="text-gray-500 mt-1">Crea e gestisci le organizzazioni</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuovo Tenant
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 rounded-lg">
              <Building2 className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{tenants.length}</p>
              <p className="text-sm text-gray-500">Tenant totali</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Building2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {tenants.filter((t) => t.status === 'active').length}
              </p>
              <p className="text-sm text-gray-500">Attivi</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {tenants.filter((t) => t.status === 'trial').length}
              </p>
              <p className="text-sm text-gray-500">In prova</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tenant List */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="px-6 py-4 border-b">
          <h2 className="font-semibold text-gray-900">Lista Tenant</h2>
        </div>

        {tenants.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessun tenant creato</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 text-primary-600 hover:underline"
            >
              Crea il primo tenant
            </button>
          </div>
        ) : (
          <div className="divide-y">
            {tenants.map((tenant) => (
              <div
                key={tenant.id}
                onClick={() => navigate(`/superadmin/tenants/${tenant.id}`)}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{tenant.name}</p>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          statusColors[tenant.status] || 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {tenant.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {tenant.email || 'Nessuna email'}
                    </p>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{tenant.users_count}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Boxes className="w-4 h-4" />
                      <span>{tenant.modules_count}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>

                {/* Link Section */}
                <div className="mt-3 ml-16 flex items-center gap-3">
                  <code className="flex-1 text-sm bg-gray-100 px-3 py-1.5 rounded-lg text-gray-600 font-mono truncate">
                    {getTenantLoginUrl(tenant.code)}
                  </code>
                  <button
                    onClick={(e) => copyLink(tenant, e)}
                    className={`p-2 rounded-lg transition-colors ${
                      copiedId === tenant.id
                        ? 'bg-green-100 text-green-600'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={copiedId === tenant.id ? 'Copiato!' : 'Copia link'}
                  >
                    {copiedId === tenant.id ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => openLink(tenant, e)}
                    className="p-2 bg-primary-100 text-primary-600 rounded-lg hover:bg-primary-200 transition-colors"
                    title="Apri in nuova scheda"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateTenantModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false)
            loadTenants()
          }}
        />
      )}
    </div>
  )
}

// Modal creazione tenant
function CreateTenantModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdTenant, setCreatedTenant] = useState<{ code: string; name: string } | null>(null)
  const [copied, setCopied] = useState(false)

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    email: '',
    phone: '',
    max_users: '10',
    admin_email: '',
    admin_password: '',
    admin_username: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      await api.post('/v1/superadmin/tenants', formData)
      setCreatedTenant({ code: formData.code, name: formData.name })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nella creazione')
    } finally {
      setLoading(false)
    }
  }

  const copyCreatedLink = async () => {
    if (!createdTenant) return
    const url = getTenantLoginUrl(createdTenant.code)
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = url
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const openCreatedLink = () => {
    if (!createdTenant) return
    const url = getTenantLoginUrl(createdTenant.code)
    window.open(url, '_blank')
  }

  // Success view dopo creazione
  if (createdTenant) {
    const loginUrl = getTenantLoginUrl(createdTenant.code)
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl w-full max-w-lg p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Tenant Creato!</h2>
            <p className="text-gray-500 mt-1">{createdTenant.name}</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-600 mb-2">Link di accesso:</p>
            <code className="block text-sm bg-white px-3 py-2 rounded-lg border text-primary-600 font-mono break-all">
              {loginUrl}
            </code>
          </div>

          <div className="flex gap-3 mb-4">
            <button
              onClick={copyCreatedLink}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                copied
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              {copied ? 'Copiato!' : 'Copia Link'}
            </button>
            <button
              onClick={openCreatedLink}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <ExternalLink className="w-5 h-5" />
              Apri Login
            </button>
          </div>

          <button
            onClick={onCreated}
            className="w-full px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Chiudi
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">Nuovo Tenant</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Info Tenant */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Informazioni Azienda</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Codice *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })
                  }
                  placeholder="acme"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Solo lettere minuscole e numeri</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Azienda *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ACME Corporation"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
            </div>

            {/* Preview URL */}
            {formData.code && (
              <div className="bg-primary-50 rounded-lg p-3">
                <p className="text-xs text-primary-700 mb-1">URL di accesso:</p>
                <code className="text-sm text-primary-800 font-mono">
                  {getTenantLoginUrl(formData.code)}
                </code>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Azienda
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="info@acme.com"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Utenti Concorrenti
              </label>
              <input
                type="number"
                min="1"
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: e.target.value })}
                placeholder="10"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Numero massimo di utenti che possono essere connessi contemporaneamente</p>
            </div>
          </div>

          {/* Admin Tenant */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium text-gray-900">Amministratore Tenant</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username Admin *
              </label>
              <input
                type="text"
                value={formData.admin_username}
                onChange={(e) => setFormData({ ...formData, admin_username: e.target.value })}
                placeholder="Mario Rossi"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Admin *
              </label>
              <input
                type="email"
                value={formData.admin_email}
                onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                placeholder="admin@acme.com"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password Admin *
              </label>
              <input
                type="password"
                value={formData.admin_password}
                onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                placeholder="••••••••"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                required
                minLength={8}
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 text-red-600 text-sm px-4 py-2 rounded-lg">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Crea Tenant
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
